import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { mappingApi } from "@services/mappingApi";
import { ApiError } from "@utils/httpClient";
import {
  triggerJsonDownload,
  triggerUrlDownload,
} from "@utils/jobResultsHelpers";
import type { JobViewModel } from "@models/job";
import type { UseJobResultsState } from "@hooks/useJobResults.types";

/** Longest wait for row saves before an Excel export. Each PATCH is cut at
 *  29 s by API Gateway and retried twice, so this only bounds a pathological
 *  case; normally the wait is one request. */
export const SAVE_FLUSH_TIMEOUT_MS = 30_000;

export interface UseResultsExportResult {
  exportingExcel: boolean;
  /** Rows whose edits were still unsaved when the export was asked for;
   *  non-null opens the prompt. */
  unsavedRows: number[] | null;
  handleExport: () => void;
  handleExportExcel: () => Promise<void>;
  closeUnsavedPrompt: () => void;
  retryUnsavedAndExport: () => Promise<void>;
  discardUnsavedAndExport: () => Promise<void>;
}

/**
 * Export handlers: a client-side JSON snapshot of the current review state,
 * and a server-generated Excel download.
 */
export const useResultsExport = (
  job: JobViewModel | null,
  results: UseJobResultsState,
): UseResultsExportResult => {
  const { t } = useTranslation();
  const [exportingExcel, setExportingExcel] = useState(false);
  const [unsavedRows, setUnsavedRows] = useState<number[] | null>(null);

  const handleExport = useCallback((): void => {
    if (!job) return;
    const payload = {
      jobId: job.jobId,
      filename: job.filename,
      exportedAt: new Date().toISOString(),
      rows: results.rows.map((row) => {
        const edit = results.edits.get(row.rowIndex);
        return {
          rowIndex: row.rowIndex,
          kind: row.kind,
          source: row.source,
          reviewStatus: edit?.reviewStatus ?? row.reviewStatus,
          chosenRank: edit?.chosenRank,
          original: {
            reviewStatus: row.reviewStatus,
            mappingStatus: row.mappingStatus,
            // The candidate the row points at. The rest of them are their own
            // endpoint and are not in memory unless a dropdown was opened.
            target: row.target,
          },
        };
      }),
    };
    triggerJsonDownload(`${job.jobId}-review.json`, payload);
  }, [job, results]);

  const runExcelExport = useCallback(async (): Promise<void> => {
    if (!job) return;
    try {
      const result = await mappingApi.exportJobResult(job.jobId);
      if (!result.downloadUrl || result.rowCount === 0) {
        notifications.show({
          color: "yellow",
          title: t("jobResultsPage.export.noDataTitle", {
            defaultValue: "Nothing to export",
          }),
          message: t("jobResultsPage.export.noData", {
            defaultValue: "There are no mapping results to export.",
          }),
          withCloseButton: true,
        });
        return;
      }
      triggerUrlDownload(result.downloadUrl, result.filename);
    } catch (err) {
      notifications.show({
        color: "red",
        title: t("jobResultsPage.export.failedTitle", {
          defaultValue: "Export failed",
        }),
        message:
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : t("jobResultsPage.export.failed", {
                  defaultValue: "Could not generate the Excel file.",
                }),
        withCloseButton: true,
      });
    }
  }, [job, t]);

  const handleExportExcel = useCallback(async (): Promise<void> => {
    if (!job) return;
    // Row actions are optimistic: the table shows Accepted immediately while
    // the PATCH is still in flight, but the Excel file is generated from
    // DynamoDB. Wait for the saves in flight instead of refusing the export,
    // and if some edits still are not saved, say which and let the reviewer
    // decide (AB#840758: the button used to stay disabled, with no reason
    // shown, for as long as a failed edit lingered).
    setExportingExcel(true);
    try {
      const unsaved = await results.flushSaves(SAVE_FLUSH_TIMEOUT_MS);
      if (unsaved.length > 0) {
        setUnsavedRows(unsaved);
        return;
      }
      await runExcelExport();
    } finally {
      setExportingExcel(false);
    }
  }, [job, results, runExcelExport]);

  const closeUnsavedPrompt = useCallback((): void => setUnsavedRows(null), []);

  const retryUnsavedAndExport = useCallback(async (): Promise<void> => {
    setUnsavedRows(null);
    results.retryAllFailed();
    await handleExportExcel();
  }, [results, handleExportExcel]);

  const discardUnsavedAndExport = useCallback(async (): Promise<void> => {
    setUnsavedRows(null);
    results.discardUnsaved();
    setExportingExcel(true);
    try {
      await runExcelExport();
    } finally {
      setExportingExcel(false);
    }
  }, [results, runExcelExport]);

  return {
    exportingExcel,
    unsavedRows,
    handleExport,
    handleExportExcel,
    closeUnsavedPrompt,
    retryUnsavedAndExport,
    discardUnsavedAndExport,
  };
};
